import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Snackbar,
  Tabs,
  Tab
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { resetLogin, userDetailsFromToken, resetDemoQuestionMetadata, resetDemoQuestionIDs } from "../../actions";
import { useDispatch, useSelector } from "react-redux";
import mixpanel from 'mixpanel-browser';
import { trackUserSignIn, trackAdConversion, trackReferralFunnel } from '../../utils/tracking';
import { clearConsumedReferralParam, getSafeNextPath } from '../../utils/attribution';
import { getActiveReferralCode, clearReferralCode } from '../../utils/referral';
import url from "../../constants/urlList";
import "./login.scss";
import Hotjar from '@hotjar/browser';
import PhoneAuthSection from './PhoneAuthSection';
import { isNativeCapacitorRuntime } from '../../services/nativeRuntime';
import {
  runNativeGoogleOAuth,
  authorizeApple,
  appleSignIn,
  isAppleAuthCancel,
  isLikelyJwt,
} from '../../services/nativeAuthService';

const googleLogo = require("../../images/google-icon.png").default;
const POST_LOGIN_PATHS = ['/daily-absite-question', '/create-quiz', '/absite-diagnostic', '/quiz-history'];
const NOTES_TO_QUIZ_PREFIX = '/notes-to-quiz/';

// Google OAuth now uses a same-tab redirect (see onGoogleSignUp / googleAuthRedirect):
// the browser leaves /login for the provider and returns to /socialAuth, so the
// intended post-login destination is stashed here to be restored on return, and
// a pending flag tells /login to finish the sign-in when it re-mounts.
// The stash is strictly one-round-trip scoped (gate B2): it is timestamped on
// write, ignored/cleared past a short TTL, and swept on any /login mount that
// is NOT completing an OAuth return — so an attempt aborted at Google can never
// misroute a later (e.g. phone) login.
const OAUTH_RETURN_PATH_KEY = 'oauthReturnPath';
const OAUTH_RETURN_PATH_AT_KEY = 'oauthReturnPathAt';
const OAUTH_RETURN_PATH_MAX_AGE_MS = 10 * 60 * 1000; // one OAuth round-trip, generously
const OAUTH_PENDING_KEY = 'oauthPending';

const clearOAuthReturnPath = () => {
  window.localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
  window.localStorage.removeItem(OAUTH_RETURN_PATH_AT_KEY);
};

// Read-and-consume the stashed destination; null unless it was written within
// the TTL (a bare or stale stash is cleared, never honored).
const takeOAuthReturnPath = () => {
  const stashed = window.localStorage.getItem(OAUTH_RETURN_PATH_KEY);
  const stashedAt = Number(window.localStorage.getItem(OAUTH_RETURN_PATH_AT_KEY));
  clearOAuthReturnPath();
  if (stashed && stashedAt && (Date.now() - stashedAt) < OAUTH_RETURN_PATH_MAX_AGE_MS) {
    return stashed;
  }
  return null;
};

// Default sign-in tab. Google stays the default (over phone) now that E3 replaced
// the popup with a same-tab redirect — the popup was the reason google-default was
// questioned for mobile-heavy referral traffic; a redirect is reliable on mobile
// Safari, so the higher-conversion Google path can lead. An explicit `?auth=phone`
// still opens phone-first.
const getInitialAuthMethod = () => {
  const auth = new URLSearchParams(window.location.search).get('auth');
  return auth === 'phone' ? 'phone' : 'google';
};

const LoginView = (props) => {
  const { onLoginRequest, onLoginSubmit } = props;
  const [userAcquisitionQuesId, setUserAcquisitionQuesId] = useState('');
  const [oauthError, setOAuthError] = useState(false);
  const [isOAuthLogin, setIsOAuthLogin] = useState(false);
  const [authMethod, setAuthMethod] = useState(getInitialAuthMethod);
  const [invitePending, setInvitePending] = useState(false);
  const { handleSubmit } = useForm();
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    const savedUserId = window.localStorage.getItem("userId");
    const userAcqQuesId = window.localStorage.getItem("userAcqQuesId");
    const isProfileComplete = window.localStorage.getItem("isProfileComplete");

    // only redirect if user has completed profile
    if (savedUserId && isProfileComplete === 'true') {
      goToPostLoginPath();
    }
    // If profile  incomplete (isProfileComplete : 'false'), stay on login page
    // so PhoneAuthSection can show the profile completion form

    if (userAcqQuesId) {
      setUserAcquisitionQuesId(userAcqQuesId);
    }

    // A referral code is present only after visiting a friend's invite link;
    // acknowledging the invite meaningfully lifts referred-signup conversion.
    if (!savedUserId && getActiveReferralCode(window.localStorage)) {
      setInvitePending(true);
      trackReferralFunnel('referral_invite_landing_viewed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Finish a same-tab Google OAuth round-trip. googleAuthRedirect (/socialAuth)
  // stashed the token + a pending flag and bounced back to /login; complete the
  // sign-in through the same resolution path the popup flow used (fetch the user
  // from the token, then navigate on success / show the error snackbar on
  // failure) so success, tracking, and error handling stay in one place.
  useEffect(() => {
    const pending = window.localStorage.getItem(OAUTH_PENDING_KEY) === 'true';
    const token = window.localStorage.getItem('token');
    if (pending && token) {
      window.localStorage.removeItem(OAUTH_PENDING_KEY);
      setIsOAuthLogin(true);
      clearReferralCode(window.localStorage);
      clearConsumedReferralParam();
      dispatch(userDetailsFromToken());
    } else {
      if (pending) {
        // Bounced back without a token — treat as a failed sign-in, don't loop.
        window.localStorage.removeItem(OAUTH_PENDING_KEY);
        setOAuthError(true);
      }
      // Not completing an OAuth return on this mount (gate B2): any stashed
      // return path belongs to an attempt that never came back (e.g. the user
      // backed out at Google). Sweep it so only the round-trip it was written
      // for can consume it — never a later phone/other login.
      clearOAuthReturnPath();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (onLoginRequest.errorMsg) {
      setTimeout(() => {
        dispatch(resetLogin());
      }, 2000);
    } else if (onLoginRequest.user) {
      mixpanel.track('Sign In', {
        $name: onLoginRequest.user?.displayName,
        $user_id: onLoginRequest.user?.displayName,
        email: onLoginRequest.user?.email,
        role: onLoginRequest.user.primaryRole
      });
      mixpanel.identify(onLoginRequest.user?.displayName);
      // Set user properties
      mixpanel.people.set({
        $email: onLoginRequest.user?.email,
        $name: onLoginRequest.user?.displayName,
        $role: onLoginRequest.user.primaryRole
      });
      mixpanel.track("Menu-create-quiz", {
        $name: onLoginRequest.user?.displayName,
        email: onLoginRequest.user?.email,
        role: onLoginRequest.user.primaryRole
      });

      // GTM and Facebook Pixel tracking for email/password login
      trackUserSignIn(onLoginRequest.user, 'email');
      trackAdConversion(onLoginRequest.user, 'email_login');

      const siteId = process.env.REACT_APP_HOTJAR_KEY;
      const hotjarVersion = process.env.REACT_APP_HOTJAR_VERSION;
      Hotjar.init(siteId, hotjarVersion);

      const userData = {
        name: onLoginRequest.user?.displayName,
        email: onLoginRequest.user?.email
      };
      window.localStorage.setItem("userdetails", JSON.stringify(userData));

      window.localStorage.setItem("token", onLoginRequest.user.accessToken);
      window.localStorage.setItem("userId", onLoginRequest.user.id);
      window.localStorage.setItem("role", onLoginRequest.user.primaryRole);

      if (userAcquisitionQuesId) {
        // goToDemoExam();
        dispatch(resetDemoQuestionMetadata());
        dispatch(resetDemoQuestionIDs()); 
        window.localStorage.removeItem("userAcqQuesId");
        window.localStorage.removeItem("userAcquisitiontoken");
      } 
      
      goToPostLoginPath();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, onLoginRequest]);

  const userDetailsFromTokenRes = useSelector((state) => {
    return state.userDetailsFromToken?.userDetails;
  });

  useEffect(() => {
    if (userDetailsFromTokenRes?.status === 200 || userDetailsFromTokenRes?.id) {
      // GTM and Facebook Pixel tracking for Google OAuth login with actual role
      if (isOAuthLogin) {
        const oauthUserData = {
          id: userDetailsFromTokenRes?.id,
          email: userDetailsFromTokenRes?.email,
          displayName: userDetailsFromTokenRes?.displayName,
          primaryRole: userDetailsFromTokenRes?.primaryRole // Use actual role from server
        };
        trackUserSignIn(oauthUserData, 'google_oauth');
        trackAdConversion(oauthUserData, 'google_oauth');
        setIsOAuthLogin(false); // Reset OAuth login flag
      }

      if (userAcquisitionQuesId) {
        if (userDetailsFromTokenRes?.id) {
          window.localStorage.setItem("userId", userDetailsFromTokenRes?.id);
          window.localStorage.setItem("role", userDetailsFromTokenRes?.primaryRole);
          window.localStorage.removeItem("userAcqQuesId");
          window.localStorage.removeItem("userAcquisitiontoken");
        }
      }
      goToPostLoginPath();
    } else if (userDetailsFromTokenRes && (userDetailsFromTokenRes?.status === 401 || !userDetailsFromTokenRes?.id)) {
      // Was a native alert('User not registered') — jarring, and it dead-ended
      // exactly the new referral users this flow is optimizing for. Surface a
      // non-blocking snackbar with a way forward (phone sign-in) instead.
      setOAuthError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetailsFromTokenRes]);

  const getNotesToQuizNextPath = () => {
    const next = new URLSearchParams(window.location.search).get('next');
    if (next && next.startsWith(NOTES_TO_QUIZ_PREFIX) && !next.startsWith('//')) {
      return next;
    }
    return undefined;
  };

  const getPostLoginPath = () => (
    getNotesToQuizNextPath() || getSafeNextPath(window.location.search, {
      allowedPathnames: POST_LOGIN_PATHS,
    }) || "/create-quiz"
  );

  const goToPostLoginPath = () => {
    // A same-tab OAuth round-trip returns to a bare /login (its original ?next=
    // is gone), so prefer the destination stashed before the redirect. The value
    // was itself produced by getPostLoginPath(), so it is already allowlist-safe;
    // takeOAuthReturnPath additionally enforces the one-round-trip TTL (gate B2)
    // and the mount sweep above removed any stash from an aborted attempt. For
    // every other flow the key is absent and behavior is unchanged.
    const stashed = takeOAuthReturnPath();
    if (stashed) {
      history.push(stashed);
      return;
    }
    history.push(getPostLoginPath());
  };


  const signIn = (data) => {
    onLoginSubmit(data);
  };

  const onGoogleSignUp = () => {
    // E3: same-tab redirect instead of window.open. The popup was blocked or
    // silently failed on mobile Safari (and mobile Chrome) — the primary,
    // mobile-heavy referral-conversion path landed on a blank state. The
    // referral `ref` is forwarded on the authorization URL EXACTLY as the popup
    // did (backend consumes it server-side during OAuth); nothing needs to
    // survive the return trip except the token, which the backend redirects
    // back to /socialAuth?token=… — the same redirect URI the popup used, so no
    // backend change is required. googleAuthRedirect handles that landing.
    const referralCode = getActiveReferralCode(window.localStorage);
    const oauthUrl = referralCode
      ? `${url.domain}/qlearn/oauth2/authorization/google?ref=${encodeURIComponent(referralCode)}`
      : `${url.domain}/qlearn/oauth2/authorization/google`;
    // Stash the intended post-login destination (the /login ?next= is lost across
    // the same-tab redirect) so goToPostLoginPath can restore it on return. The
    // timestamp scopes the stash to this round-trip (gate B2): consumption
    // ignores it past a short TTL, and any /login mount that isn't completing an
    // OAuth return sweeps it.
    try {
      window.localStorage.setItem(OAUTH_RETURN_PATH_KEY, getPostLoginPath());
      window.localStorage.setItem(OAUTH_RETURN_PATH_AT_KEY, String(Date.now()));
    } catch (e) {
      // localStorage unavailable → return path falls back to the default; the
      // sign-in itself still proceeds.
    }
    window.location.assign(oauthUrl);
  };

  // ── Dark native trio (Step 2) ───────────────────────────────────────────────
  // A strict native sign-in card (Apple / Google / Phone, no email surface),
  // reachable ONLY inside the Capacitor shell AND with the exact build flag set.
  // On web `isNativeCapacitorRuntime()` is false, so the whole branch is inert
  // and the existing web login renders byte/DOM-identically (flag-off parity).
  const nativeTrioEnabled =
    isNativeCapacitorRuntime() &&
    process.env.REACT_APP_QLEARN_IOS_SIGNIN_TRIO === 'true';

  // 'options' = the three-button card; 'phone' = PhoneAuthSection + a back control.
  const [nativeView, setNativeView] = useState('options');
  // The active native-Google controller, retained so it is disposed exactly once
  // on unmount and before any retry (the service owns a single live attempt).
  const googleControllerRef = useRef(null);
  // Monotonic attempt token. `runNativeGoogleOAuth()` resolves its controller only
  // AFTER serialized admission, so a retry/unmount/back can occur while admission is
  // still pending. Bumping this token supersedes a pending attempt: when its
  // controller finally arrives, the attempt disposes it and never persists a result.
  const googleAttemptRef = useRef(0);
  // Guards against disposing the same controller twice (idempotent teardown).
  const disposedControllersRef = useRef(new WeakSet());
  // Guards async completions from setting state after the surface unmounts.
  const isMountedRef = useRef(true);

  // Dispose a specific controller exactly once, whichever path reaches it first
  // (attempt finally, retry supersede, back, or unmount).
  const safeDisposeController = (controller) => {
    if (!controller || typeof controller.dispose !== 'function') return;
    if (disposedControllersRef.current.has(controller)) return;
    disposedControllersRef.current.add(controller);
    try {
      controller.dispose();
    } catch (e) {
      // dispose is best-effort teardown; a throw here must not surface to the UI.
    }
  };

  const disposeGoogleController = () => {
    const controller = googleControllerRef.current;
    googleControllerRef.current = null;
    safeDisposeController(controller);
  };

  // Supersede any pending/settled native-Google attempt: bump the token so a
  // still-pending admission disposes its eventual controller instead of persisting,
  // and dispose a controller already retained in the ref.
  const supersedeGoogleAttempt = () => {
    googleAttemptRef.current += 1;
    disposeGoogleController();
  };

  // Dispose any in-flight native-Google attempt when the login surface unmounts.
  useEffect(() => () => {
    isMountedRef.current = false;
    supersedeGoogleAttempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Complete a native sign-in through the SAME resolution path the web flows use:
  // persist the session token and hand off to userDetailsFromToken (the effect
  // above navigates on success). No linking/identity logic lives here — the
  // backend owns link-by-verified-email-or-create.
  const finishNativeSession = ({ token, id, isProfileComplete }) => {
    if (token) window.localStorage.setItem('token', token);
    if (id) window.localStorage.setItem('userId', id);
    if (typeof isProfileComplete !== 'undefined') {
      window.localStorage.setItem('isProfileComplete', isProfileComplete ? 'true' : 'false');
    }
    clearReferralCode(window.localStorage);
    clearConsumedReferralParam();
    dispatch(userDetailsFromToken());
  };

  // Native Google: consume the reviewed Step-1 deep-link service. `runNativeGoogleOAuth`
  // returns a Promise that resolves the controller AFTER serialized admission — it is
  // NOT the controller itself. We await it while holding this attempt's token: if a
  // retry, unmount, or back-to-options supersedes the attempt during admission, the
  // eventual controller is disposed and its result is never persisted. The service
  // validates state (CSRF) + JWT-shape internally; we additionally guard the returned
  // access token with the Step-1 isLikelyJwt seam before persisting.
  const onNativeGoogleSignIn = async () => {
    // Dispose a settled prior controller, then claim a fresh attempt token.
    disposeGoogleController();
    const attempt = (googleAttemptRef.current += 1);
    const isCurrent = () => isMountedRef.current && googleAttemptRef.current === attempt;
    setOAuthError(false);
    let controller;
    try {
      const [{ App: appModule }, { Browser: browserModule }] = await Promise.all([
        import('@capacitor/app'),
        import('@capacitor/browser'),
      ]);
      const referralCode = getActiveReferralCode(window.localStorage);
      // Await the deferred admission to obtain the controller.
      controller = await runNativeGoogleOAuth({
        appModule,
        browserModule,
        domain: url.domain,
        referralCode,
      });
      // Superseded (retry/unmount/back) while admission was pending: own the eventual
      // controller only to tear it down; never persist its result.
      if (!isCurrent()) {
        safeDisposeController(controller);
        return;
      }
      googleControllerRef.current = controller;
      const outcome = await controller.result;
      if (!isCurrent()) return;
      // Persist only a well-formed JWT (Step-1 seam; no reimplemented parsing).
      if (outcome && outcome.ok && isLikelyJwt(outcome.token)) {
        setIsOAuthLogin(true);
        finishNativeSession({ token: outcome.token });
      } else {
        setOAuthError(true);
      }
    } catch (e) {
      if (isCurrent()) setOAuthError(true);
    } finally {
      // Dispose this attempt's controller exactly once (idempotent).
      safeDisposeController(controller);
    }
  };

  // Native Apple: consume the reviewed Step-1 seam (plugin/token/cancel logic all
  // live in the service — never reimplemented here). A user-cancelled sheet is
  // silent; every other failure surfaces the shared error snackbar.
  const onAppleSignIn = async () => {
    setOAuthError(false);
    try {
      const response = await authorizeApple();
      if (!isMountedRef.current) return;
      // Guard the Apple identity token BEFORE exchange (Step-1 isLikelyJwt seam;
      // no reimplemented parsing). A malformed token never reaches the backend.
      if (!response || !isLikelyJwt(response.identityToken)) {
        setOAuthError(true);
        return;
      }
      const fullName = [response.givenName, response.familyName].filter(Boolean).join(' ');
      const session = await appleSignIn({
        identityToken: response.identityToken,
        ...(fullName ? { fullName } : {}),
      });
      if (!isMountedRef.current) return;
      // Guard the returned QLearn access token BEFORE persistence.
      if (!session || session.errStatus || !isLikelyJwt(session.accessToken)) {
        setOAuthError(true);
        return;
      }
      finishNativeSession({
        token: session.accessToken,
        id: session.id,
        isProfileComplete: session.isProfileComplete,
      });
    } catch (err) {
      if (isAppleAuthCancel(err)) return; // user cancelled — silent, per spec
      if (isMountedRef.current) setOAuthError(true);
    }
  };

  const goBackToNativeOptions = () => {
    // Leaving the options view supersedes any pending Google admission so a late
    // controller cannot complete a sign-in the user has navigated away from.
    supersedeGoogleAttempt();
    setNativeView('options');
  };

  // Every real transition INTO native Phone auth (the direct Phone choice AND the
  // snackbar fallback) must supersede any pending/settled Google attempt BEFORE the
  // view changes: a late deferred Google controller/result is then disposed exactly
  // once and can never persist a token, dispatch session resolution, or navigate
  // over PhoneAuthSection.
  const enterNativePhoneView = () => {
    supersedeGoogleAttempt();
    setNativeView('phone');
  };

  return (
    <>
      <Snackbar open={oauthError} onClose={() => setOAuthError(false)} autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <>
          <Box className="success-warn" data-testid="oauth-error">
            {nativeTrioEnabled ? (
              <>
                We couldn&apos;t sign you in. Please try again, or continue with
                phone.
                <Button
                  size="small"
                  className="oauth-error-action"
                  onClick={() => { enterNativePhoneView(); setOAuthError(false); }}
                >
                  Continue with phone
                </Button>
              </>
            ) : (
              <>
                We couldn&apos;t sign you in with Google. Please try again, or use
                phone sign-in instead.
                <Button
                  size="small"
                  className="oauth-error-action"
                  onClick={() => { setAuthMethod('phone'); setOAuthError(false); }}
                >
                  Use phone sign-in
                </Button>
              </>
            )}
          </Box>
        </>
      </Snackbar>
      <Box className="login-body">
        <Box component="div" className="app-name">
          QLearn
        </Box>
        <Box component="div" className="app-tagline">
          Board-exam question practice by Medlearnity
        </Box>

        <Box component="div" className="login-container">
          {invitePending && (
            <Box
              data-testid="referral-invite-banner"
              style={{
                background: '#e8f5e9', color: '#1b5e20', borderRadius: 4,
                padding: '8px 12px', marginBottom: 16, fontSize: 14, textAlign: 'center'
              }}
            >
              🎁 A friend invited you to QLearn — sign up below and get 7 days of Pro free.
            </Box>
          )}
          <Box component="div" className="error-container">
            {onLoginRequest.errorMsg}
          </Box>

          {getSafeNextPath(window.location.search, {
            allowedPathnames: POST_LOGIN_PATHS,
          }) && (
            <Box component="div" className="auth-continue-note">
              Sign in to continue where you left off.
            </Box>
          )}

          {nativeTrioEnabled ? (
            <Box className="native-auth-card" data-testid="native-auth-card">
              {nativeView === 'options' ? (
                <Box className="native-auth-options">
                  {/* Guideline 4.8: the privacy-preserving option is offered first,
                      with equal-or-greater prominence, whenever Google is shown. */}
                  <Button
                    variant="contained"
                    className="native-auth-btn native-apple-btn"
                    data-testid="native-apple-btn"
                    onClick={onAppleSignIn}
                  >
                    Sign in with Apple
                  </Button>
                  <Button
                    variant="outlined"
                    className="native-auth-btn native-google-btn"
                    data-testid="native-google-btn"
                    onClick={onNativeGoogleSignIn}
                  >
                    <img alt="google" src={googleLogo} width='20' height='20' className="logo" ></img>
                    Sign in with Google
                  </Button>
                  <Button
                    variant="outlined"
                    className="native-auth-btn native-phone-btn"
                    data-testid="native-phone-btn"
                    onClick={enterNativePhoneView}
                  >
                    Continue with Phone
                  </Button>
                </Box>
              ) : (
                <Box className="native-auth-phone">
                  <Button
                    className="native-back-btn"
                    data-testid="native-back-to-options"
                    onClick={goBackToNativeOptions}
                  >
                    ← Back to options
                  </Button>
                  <PhoneAuthSection />
                </Box>
              )}
            </Box>
          ) : (
          <>
          <Tabs
            value={authMethod}
            onChange={(_, newValue) => setAuthMethod(newValue)}
            centered
            indicatorColor="primary"
            textColor="primary"
            style={{ marginBottom: '20px' }}
          >
            <Tab label="Google Sign In" value="google" />
            <Tab label="Phone Sign In" value="phone" />
          </Tabs>

          {authMethod === 'google' && (
            <form noValidate className="login-form" onSubmit={handleSubmit(signIn)}>
              <Button
                variant="outlined"
                color="secondary"
                className="google-btn"
                id="google_login_btn"
                onClick={onGoogleSignUp}
              >
                <img alt="google" src={googleLogo} width='30' height='30' className="logo" ></img>
                Sign In with Google
              </Button>
              {/* E1: email/password registration was retired (/register now
                  redirects here). Reassure people who originally signed up with
                  email — Google sign-in with the same address adopts their
                  existing account (QLearn-service UserManager.registerOAuth2User
                  matches on email and keeps account + history). */}
              <Box component="p" className="email-user-hint" data-testid="email-user-hint">
                Previously signed up with email? Use Google sign-in with the same
                address — your account and history carry over.
              </Box>
            </form>
          )}

          {authMethod === 'phone' && (
            <PhoneAuthSection />
          )}
          </>
          )}

          <Box component="p" className="auth-consent">
            By signing in you agree to the QLearn{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>{" "}
            and{" "}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </Box>

        </Box>

        {/* Crawlable link out to the indexable marketing surface (LEG-1 §5.S3):
            the bare root renders this login view for anonymous visitors, so a
            real anchor here gives crawlers a path to medlearnity.com/qlearn/. */}
        <Box component="footer" className="login-marketing-footer">
          <a href="https://medlearnity.com/qlearn/">
            Learn more about QLearn by Medlearnity
          </a>
        </Box>
      </Box></>

  );
};

export default LoginView;
